import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { format, startOfDay, endOfDay } from 'date-fns';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { translateZoneName, clearTranslationCache } from '@medical/shared/utils/translateZoneName';

const DashboardContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

const WelcomeSection = styled.div`
  background: #0B63C5;
  color: white;
  padding: 2rem;
  border-radius: 1rem;
  margin-bottom: 2rem;
  text-align: center;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: 1.125rem;
  opacity: 0.9;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  text-align: center;

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 0.5rem;
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  background: ${props => props.color || '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const ActionButton = styled.button`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  
  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const ActionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 0.5rem;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const ActionText = styled.span`
  font-weight: 500;
  color: #374151;
`;

const RecentLogs = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const LogItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border: 1px solid #f3f4f6;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const LogInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const PainIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => 
    props.color === '#ef4444' ? '#ef4444' :
    props.color === '#f59e0b' ? '#f59e0b' :
    props.color === '#60a5fa' ? '#60a5fa' :
    props.color === '#10b981' ? '#10b981' :
    props.color === 'red' ? '#ef4444' :
    props.color === 'yellow' ? '#f59e0b' :
    props.color === 'blue' ? '#60a5fa' :
    props.color === 'green' ? '#10b981' : '#e5e7eb'
  };
`;

const LogDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const LogZone = styled.span`
  font-weight: 500;
  color: #374151;
`;

const LogTime = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
`;

const LogLevel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => 
    props.level === 'worst-pain' ? '#ef4444' :
    props.level === 'severe' ? '#f97316' :
    props.level === 'moderate' ? '#f59e0b' :
    props.level === 'mild' ? '#60a5fa' :
    props.level === 'no-pain' ? '#10b981' :
    '#6b7280'
  };
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
`;

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const [painData, setPainData] = useState([]);
  const [zoneStats, setZoneStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render on language change

  // Listen to language changes and refresh
  useEffect(() => {
    const handleLanguageChange = () => {
      clearTranslationCache(); // Clear cache when language changes
      setRefreshKey(prev => prev + 1); // Force component re-render
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    fetchPainData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPainData = async () => {
    try {
      setLoading(true);
      setError(null);
      const today = new Date();
      const startOfToday = startOfDay(today);
      const endOfToday = endOfDay(today);
      
      const response = await axios.get('/api/pain-logs', {
        params: {
          startDate: startOfToday.toISOString(),
          endDate: endOfToday.toISOString()
        }
      });
      
      // Handle different response structures
      const logs = response.data?.painLogs || response.data || [];
      
      // Ensure logs is an array
      if (!Array.isArray(logs)) {
        console.warn('Expected array but got:', typeof logs, logs);
        setPainData([]);
        setZoneStats({});
        return;
      }
      
      setPainData(logs);
      
      // Calculate zone statistics
      const stats = {};
      logs.forEach(log => {
        if (log && log.zoneId) {
          stats[log.zoneId] = {
            painLevel: log.painLevel,
            painColor: log.painColor,
            timestamp: log.timestamp || log.date
          };
        }
      });
      setZoneStats(stats);
      
    } catch (err) {
      console.error('Error fetching pain data:', err);
      const errorMessage = err.response?.data?.message || err.message || t('dashboard.errors.loadFailed');
      setError(errorMessage);
      setPainData([]);
      setZoneStats({});
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return <LoadingSpinner text={t('dashboard.loading')} />;
  }

  if (error) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>{t('dashboard.errors.title')}</h2>
          <p>{error}</p>
          <button onClick={fetchPainData} className="btn btn-primary">
            {t('dashboard.actions.retry')}
          </button>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <WelcomeSection>
        <WelcomeTitle>{t('dashboard.welcome.title')}</WelcomeTitle>
        <WelcomeSubtitle>
          {t('dashboard.welcome.subtitle')}
        </WelcomeSubtitle>
      </WelcomeSection>

      <StatsGrid>
        <StatCard>
          <StatIcon color="#3b82f6">📊</StatIcon>
          <StatValue>{painData.length || 0}</StatValue>
          <StatLabel>{t('dashboard.stats.logsToday')}</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon color="#ef4444">🔴</StatIcon>
          <StatValue>{painData.filter(log => log.painLevel === 'worst-pain').length || 0}</StatValue>
          <StatLabel>{t('dashboard.stats.worstPain')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#f97316">🟠</StatIcon>
          <StatValue>{painData.filter(log => log.painLevel === 'severe').length || 0}</StatValue>
          <StatLabel>{t('dashboard.stats.severePain')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#f59e0b">🟡</StatIcon>
          <StatValue>{painData.filter(log => log.painLevel === 'moderate').length || 0}</StatValue>
          <StatLabel>{t('dashboard.stats.moderatePain')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#60a5fa">🔵</StatIcon>
          <StatValue>{painData.filter(log => log.painLevel === 'mild').length || 0}</StatValue>
          <StatLabel>{t('dashboard.stats.mild')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#10b981">🟢</StatIcon>
          <StatValue>{painData.filter(log => log.painLevel === 'no-pain').length || 0}</StatValue>
          <StatLabel>{t('dashboard.stats.noPain')}</StatLabel>
        </StatCard>
      </StatsGrid>

      <QuickActions>
        <ActionButton onClick={() => window.location.href = '/body-diagram'}>
          <ActionIcon>🎯</ActionIcon>
          <ActionText>{t('dashboard.actions.bodyDiagram')}</ActionText>
        </ActionButton>
        
        <ActionButton onClick={() => window.location.href = '/pain-logs'}>
          <ActionIcon>📋</ActionIcon>
          <ActionText>{t('dashboard.actions.viewLogs')}</ActionText>
        </ActionButton>
        
        <ActionButton onClick={() => window.location.href = '/statistics'}>
          <ActionIcon>📈</ActionIcon>
          <ActionText>{t('dashboard.actions.viewStatistics')}</ActionText>
        </ActionButton>
        
        <ActionButton onClick={() => window.location.href = '/history'}>
          <ActionIcon>📚</ActionIcon>
          <ActionText>{t('dashboard.actions.history')}</ActionText>
        </ActionButton>
      </QuickActions>

      <RecentLogs>
        <SectionTitle>{t('dashboard.logs.title')}</SectionTitle>
        {painData && painData.length > 0 ? (
          (painData || []).slice(0, 5).map((log, index) => {
            if (!log) return null;
            const timestamp = log.timestamp || log.date;
            let formattedTime = '';
            try {
              if (timestamp) {
                const date = new Date(timestamp);
                if (!isNaN(date.getTime())) {
                  formattedTime = format(date, 'h:mm a');
                }
              }
            } catch (err) {
              console.warn('Error formatting timestamp:', err);
            }
            
            return (
              <LogItem key={`${log._id || index}-${i18n.language}-${refreshKey}`}>
                <LogInfo>
                  <PainIndicator color={log.painColor} />
                  <LogDetails>
                    <LogZone>{translateZoneName(log.zoneName, t) || 'Unknown Zone'}</LogZone>
                    {formattedTime && (
                      <LogTime>{formattedTime}</LogTime>
                    )}
                  </LogDetails>
                </LogInfo>
                <LogLevel level={log.painLevel}>
                  {log.painLevel === 'worst-pain' ? t('dashboard.stats.worstPain') :
                   log.painLevel === 'severe' ? t('dashboard.stats.severePain') :
                   log.painLevel === 'moderate' ? t('dashboard.stats.moderatePain') :
                   log.painLevel === 'mild' ? t('dashboard.stats.mild') :
                   log.painLevel === 'no-pain' ? t('dashboard.stats.noPain') : t('dashboard.stats.unknown')}
                </LogLevel>
              </LogItem>
            );
          })
        ) : (
          <EmptyState>
            <EmptyIcon>📝</EmptyIcon>
            <EmptyTitle>{t('dashboard.logs.emptyTitle')}</EmptyTitle>
            <EmptyText>{t('dashboard.logs.emptyText')}</EmptyText>
          </EmptyState>
        )}
      </RecentLogs>

    </DashboardContainer>
  );
};

export default Dashboard;
